import React, { useEffect, useMemo, useState } from 'react';
import { apiRequest } from '../../services/apiClient';
import { useFetch } from '../../hooks/useFetch';
import Button from '../ui/Button';
import { StatusPill } from '../ui/Status';
import DataTable from '../ui/DataTable';
import DirectorPanelState from './DirectorPanelState';
import DirectorModal from './DirectorModal';
import { useConfirm } from '../ui/ConfirmDialog';
import {
  DIRECTOR_DATA_UPDATED_EVENT,
  notifyDirectorAdminToast,
  notifyDirectorDataUpdated,
  notifyDirectorScorecardLayoutUpdated
} from '../../utils/directorEvents';

const FORMAT_OPTIONS = [
  { value: 'number', label: 'Number' },
  { value: 'currency', label: 'Currency' },
  { value: 'percent', label: 'Percent' },
  { value: 'points', label: 'Points' }
];

const COMPARISON_OPTIONS = [
  { value: 'higher_better', label: 'Higher is Better' },
  { value: 'lower_better', label: 'Lower is Better' }
];

const EMPTY_FORM = {
  kpiKey: '',
  name: '',
  category: '',
  section: 'General',
  unit: '',
  formatType: 'number',
  comparisonLogic: 'higher_better',
  isDistributable: true,
  supportsWeeklyGoal: true,
  supportsMonthlyGoal: true,
  supportsYearlyGoal: true,
  showOnDashboard: false,
  description: '',
  sortOrder: '0'
};

const SCORECARD_CARD_LIMIT_STORAGE_KEY = 'directorScorecardCardLimit';

function formatDashboardOrder(kpi) {
  return Number.isFinite(Number(kpi.sortOrder)) ? Number(kpi.sortOrder) : 0;
}

function getStoredScorecardCardLimit() {
  try {
    const storedValue = Number(window.localStorage.getItem(SCORECARD_CARD_LIMIT_STORAGE_KEY));
    if (Number.isFinite(storedValue)) {
      return Math.max(1, Math.min(8, Math.trunc(storedValue)));
    }
  } catch (error) {
    return 4;
  }

  return 4;
}

function DirectorKpisPanel() {
  const confirm = useConfirm();
  const [kpis, setKpis] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [duplicateMatch, setDuplicateMatch] = useState(null);
  const [filter, setFilter] = useState('active');
  const [search, setSearch] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [banner, setBanner] = useState('');
  const [error, setError] = useState('');
  const [scorecardCardLimit, setScorecardCardLimit] = useState(() => getStoredScorecardCardLimit());

  const { data: fetchedKpis, isLoading, error: fetchError, refetch } = useFetch(
    '/api/kpis?includeInactive=true'
  );

  useEffect(() => {
    if (fetchedKpis) {
      setKpis(fetchedKpis);
      setError('');
    }
  }, [fetchedKpis]);

  useEffect(() => {
    if (fetchError) setError(fetchError);
  }, [fetchError]);

  useEffect(() => {
    const handler = () => refetch();
    window.addEventListener(DIRECTOR_DATA_UPDATED_EVENT, handler);
    return () => window.removeEventListener(DIRECTOR_DATA_UPDATED_EVENT, handler);
  }, [refetch]);

  const visibleKpis = useMemo(() => {
    const searchTerm = search.trim().toLowerCase();
    return kpis.filter(kpi => {
      if (filter === 'active' && !kpi.isActive) {
        return false;
      }
      if (filter === 'inactive' && kpi.isActive) {
        return false;
      }

      if (!searchTerm) {
        return true;
      }

      return [
        kpi.name,
        kpi.kpiKey,
        kpi.category,
        kpi.section,
        kpi.unit
      ].some(value => String(value || '').toLowerCase().includes(searchTerm));
    });
  }, [filter, kpis, search]);

  const activeCount = useMemo(() => kpis.filter(kpi => kpi.isActive).length, [kpis]);
  const dashboardShownCount = useMemo(
    () => kpis.filter(kpi => kpi.isActive && kpi.showOnDashboard).length,
    [kpis]
  );

  const handleFieldChange = (field, value) => {
    setForm(current => ({ ...current, [field]: value }));
  };

  const handleScorecardCardLimitChange = (value) => {
    const nextLimit = Math.max(1, Math.min(8, Number(value) || 1));
    setScorecardCardLimit(nextLimit);

    try {
      window.localStorage.setItem(SCORECARD_CARD_LIMIT_STORAGE_KEY, String(nextLimit));
    } catch (storageError) {
      // Keep the scorecard limit in memory if storage is unavailable.
    }

    notifyDirectorScorecardLayoutUpdated();
    setBanner(`Scorecard card limit updated to ${nextLimit}.`);
  };

  useEffect(() => {
    if (editingId) {
      setDuplicateMatch(null);
      return;
    }

    const normalizedKey = form.kpiKey.trim().toLowerCase();
    const normalizedName = form.name.trim().toLowerCase();
    const match = kpis.find(kpi => {
      const keyMatch = normalizedKey && String(kpi.kpiKey || '').trim().toLowerCase() === normalizedKey;
      const nameMatch = normalizedName && String(kpi.name || '').trim().toLowerCase() === normalizedName;
      return keyMatch || nameMatch;
    }) || null;

    setDuplicateMatch(match);
  }, [editingId, form.kpiKey, form.name, kpis]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setDuplicateMatch(null);
  };

  const closeModal = () => {
    resetForm();
    setIsModalOpen(false);
  };

  const openCreateModal = () => {
    resetForm();
    setBanner('');
    setError('');
    setIsModalOpen(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setBanner('');
    setIsSubmitting(true);

    try {
      const payload = {
        ...form,
        sortOrder: Number(form.sortOrder || 0)
      };

      const nextKpi = editingId
        ? await apiRequest(`/api/kpis/${editingId}`, {
          method: 'PATCH',
          body: JSON.stringify(payload)
        })
        : await apiRequest('/api/kpis', {
          method: 'POST',
          body: JSON.stringify(payload)
        });

      setKpis(current => {
        const otherRows = current.filter(kpi => kpi.id !== nextKpi.id);
        return [...otherRows, nextKpi].sort((a, b) => (
          `${String(a.sortOrder).padStart(4, '0')}-${a.category}-${a.name}`
            .localeCompare(`${String(b.sortOrder).padStart(4, '0')}-${b.category}-${b.name}`)
        ));
      });
      setBanner(editingId ? `Updated KPI ${nextKpi.name}.` : `Added KPI ${nextKpi.name}.`);
      notifyDirectorAdminToast(editingId ? `Updated KPI ${nextKpi.name}.` : `Added KPI ${nextKpi.name}.`);
      notifyDirectorDataUpdated('Director dashboard refreshed for KPI changes.');
      closeModal();
    } catch (requestError) {
      notifyDirectorAdminToast(requestError.message || 'Unable to save KPI.', 'error');
      setError(requestError.message || 'Unable to save KPI.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (kpi) => {
    setEditingId(kpi.id);
    setForm({
      kpiKey: kpi.kpiKey,
      name: kpi.name,
      category: kpi.category,
      section: kpi.section,
      unit: kpi.unit,
      formatType: kpi.formatType,
      comparisonLogic: kpi.comparisonLogic,
      isDistributable: kpi.isDistributable,
      supportsWeeklyGoal: kpi.supportsWeeklyGoal,
      supportsMonthlyGoal: kpi.supportsMonthlyGoal,
      supportsYearlyGoal: kpi.supportsYearlyGoal,
      showOnDashboard: kpi.showOnDashboard,
      description: kpi.description || '',
      sortOrder: String(kpi.sortOrder ?? 0)
    });
    setDuplicateMatch(null);
    setBanner('');
    setError('');
    setIsModalOpen(true);
  };

  const handleToggleActive = async (kpi) => {
    setError('');
    setBanner('');

    try {
      const updated = await apiRequest(`/api/kpis/${kpi.id}/active`, {
        method: 'PATCH',
        body: JSON.stringify({ isActive: !kpi.isActive })
      });

      setKpis(current => current.map(item => (item.id === updated.id ? updated : item)));
      setBanner(
        updated.isActive
          ? `${updated.name} reactivated and available for new goals.`
          : `${updated.name} deactivated. History remains visible but it will not appear for new goals.`
      );
      notifyDirectorDataUpdated('Director dashboard refreshed for KPI changes.');
      notifyDirectorAdminToast(
        updated.isActive
          ? `${updated.name} reactivated.`
          : `${updated.name} deactivated.`,
        'success',
        {
          label: 'Undo',
          onClick: async () => {
            const reverted = await apiRequest(`/api/kpis/${updated.id}/active`, {
              method: 'PATCH',
              body: JSON.stringify({ isActive: kpi.isActive })
            });

            setKpis(current => current.map(item => (item.id === reverted.id ? reverted : item)));
            setBanner(
              reverted.isActive
                ? `${reverted.name} reactivated and available for new goals.`
                : `${reverted.name} deactivated. History remains visible but it will not appear for new goals.`
            );
            notifyDirectorDataUpdated('Director dashboard refreshed for KPI changes.');
            notifyDirectorAdminToast(
              reverted.isActive
                ? `${reverted.name} reactivated.`
                : `${reverted.name} deactivated.`
            );
          }
        }
      );
    } catch (requestError) {
      notifyDirectorAdminToast(requestError.message || 'Unable to update KPI status.', 'error');
      setError(requestError.message || 'Unable to update KPI status.');
    }
  };

  const handleDelete = async (kpi) => {
    const confirmed = await confirm(
      `Delete KPI "${kpi.name}"? This removes it from the definitions library. It can only be deleted if it has no historical usage.`,
      { title: 'Delete KPI', confirmLabel: 'Delete', tone: 'danger' }
    );
    if (!confirmed) {
      return;
    }

    setError('');
    setBanner('');

    try {
      await apiRequest(`/api/kpis/${kpi.id}`, { method: 'DELETE' });
      setKpis(current => current.filter(item => item.id !== kpi.id));
      if (editingId === kpi.id) {
        closeModal();
      }
      setBanner(`${kpi.name} deleted. It had no historical usage.`);
      notifyDirectorAdminToast(`${kpi.name} deleted.`);
      notifyDirectorDataUpdated('Director dashboard refreshed for KPI changes.');
    } catch (requestError) {
      notifyDirectorAdminToast(requestError.message || 'Unable to delete KPI.', 'error');
      setError(requestError.message || 'Unable to delete KPI.');
    }
  };

  return (
    <div className="director-admin-stack">
      <DirectorPanelState kind="loading" message={isLoading ? 'Loading KPI definitions...' : ''} />
      <DirectorPanelState kind="error" message={error} />
      <DirectorPanelState kind="success" message={!error ? banner : ''} />

      <div className="director-split-grid">
        <section className="director-card">
          <div className="role-section-header">
            <h2>KPI Actions</h2>
            <span>{activeCount} active</span>
          </div>
          <p className="role-section-copy">
            KPIs define the metric itself. Goals for each KPI are managed separately in the Goals tab, and actuals are entered in the Actuals tab.
          </p>
          <div className="director-form-actions">
            <Button onClick={openCreateModal}>
              Add KPI
            </Button>
          </div>
        </section>

        <section className="director-card director-note-card">
          <div className="role-section-header">
            <h2>Scorecard Display</h2>
            <span>{dashboardShownCount} shown</span>
          </div>
          <div className="director-form-grid">
            <label>
              Scorecard Card Limit
              <select
                value={scorecardCardLimit}
                onChange={(event) => handleScorecardCardLimitChange(event.target.value)}
              >
                {[1, 2, 3, 4, 5, 6, 7, 8].map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </label>
          </div>
          <ul className="director-bullet-list">
            <li>`Show on Dashboard` controls which KPIs are eligible for the scorecard highlight cards.</li>
            <li>`Display Order` controls which highlight cards appear first.</li>
            <li>`Scorecard Card Limit` controls highlight cards only. The full KPI table still shows all active eligible KPIs.</li>
          </ul>
        </section>
      </div>

      <section className="director-card">
        <div className="role-section-header">
          <h2>KPI Directory</h2>
          <span>{visibleKpis.length} shown</span>
        </div>
        <div className="director-toolbar">
          <div className="director-toolbar-group">
            <label htmlFor="kpi-filter">Filter</label>
            <select id="kpi-filter" value={filter} onChange={(event) => setFilter(event.target.value)}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="all">All</option>
            </select>
          </div>
          <div className="director-toolbar-group director-toolbar-search">
            <label htmlFor="kpi-search">Search</label>
            <input
              id="kpi-search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Name, key, category..."
            />
          </div>
        </div>
        <DataTable
          tableClassName="director-kpi-table"
          emptyMessage="No KPI definitions match the current filter."
          isEmpty={!visibleKpis.length}
          columns={[
            { label: 'Name', className: 'director-kpi-table-col-name' },
            { label: 'Key', className: 'director-kpi-table-col-key' },
            { label: 'Category', className: 'director-kpi-table-col-category' },
            { label: 'Section', className: 'director-kpi-table-col-section' },
            { label: 'Format', className: 'director-kpi-table-col-format' },
            { label: 'Order', className: 'director-kpi-table-col-order' },
            { label: 'Period Support', className: 'director-kpi-table-col-period' },
            { label: 'Dashboard', className: 'director-kpi-table-col-dashboard' },
            { label: 'Lifecycle', className: 'director-kpi-table-col-status' },
            { label: 'Actions', className: 'director-kpi-table-col-actions' }
          ]}
        >
          {visibleKpis.map(kpi => (
            <tr key={kpi.id}>
              <td className="director-kpi-table-col-name">
                <strong>{kpi.name}</strong>
                {kpi.description && <div className="director-muted">{kpi.description}</div>}
              </td>
              <td className="director-kpi-table-col-key">{kpi.kpiKey}</td>
              <td className="director-kpi-table-col-category">{kpi.category}</td>
              <td className="director-kpi-table-col-section">{kpi.section}</td>
              <td className="director-kpi-table-col-format">{FORMAT_OPTIONS.find(option => option.value === kpi.formatType)?.label || kpi.formatType}</td>
              <td className="director-kpi-table-col-order">{formatDashboardOrder(kpi)}</td>
              <td className="director-kpi-table-col-period">
                {[
                  kpi.supportsMonthlyGoal ? 'M' : null,
                  kpi.supportsYearlyGoal ? 'Y' : null
                ].filter(Boolean).join(' / ')}
              </td>
              <td className="director-kpi-table-col-dashboard">
                <StatusPill tone={kpi.showOnDashboard ? 'active' : 'inactive'}>
                  {kpi.showOnDashboard ? 'shown' : 'hidden'}
                </StatusPill>
              </td>
              <td className="director-kpi-table-col-status">
                <StatusPill tone={kpi.isActive ? 'active' : 'inactive'}>
                  {kpi.isActive ? 'active' : 'inactive'}
                </StatusPill>
              </td>
              <td className="director-kpi-table-col-actions">
                <div className="director-table-actions">
                  <Button size="sm" variant="secondary" onClick={() => handleEdit(kpi)}>
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant={kpi.isActive ? 'danger' : 'secondary'}
                    onClick={() => handleToggleActive(kpi)}
                  >
                    {kpi.isActive ? 'Deactivate' : 'Activate'}
                  </Button>
                  <Button size="sm" variant="danger" onClick={() => handleDelete(kpi)}>
                    Delete
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </DataTable>
      </section>

      {isModalOpen ? (
        <DirectorModal
          title={editingId ? 'Edit KPI' : 'Add KPI'}
          badge={editingId ? 'Update' : 'New'}
          onClose={closeModal}
        >
          {duplicateMatch && (
            <div className="director-inline-banner warning">
              {form.kpiKey.trim() && String(duplicateMatch.kpiKey || '').trim().toLowerCase() === form.kpiKey.trim().toLowerCase()
                ? <>KPI key <strong>{duplicateMatch.kpiKey}</strong> already exists for <strong>{duplicateMatch.name}</strong>.</>
                : <>KPI name <strong>{duplicateMatch.name}</strong> already exists{duplicateMatch.kpiKey ? <> under key <strong>{duplicateMatch.kpiKey}</strong></> : null}.</>
              }
              <div className="director-inline-banner-actions">
                <Button size="sm" variant="secondary" onClick={() => handleEdit(duplicateMatch)}>
                  Edit Existing KPI
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    handleFieldChange('kpiKey', '');
                    handleFieldChange('name', '');
                  }}
                >
                  Clear Fields
                </Button>
              </div>
            </div>
          )}

          <p className="role-section-copy">
            KPIs define the metric itself. Goals for each KPI are managed separately in the Goals tab, and actuals are entered in the Actuals tab.
          </p>

          <form className="director-form-grid" onSubmit={handleSubmit}>
            <label>
              KPI Key
              <input value={form.kpiKey} onChange={(event) => handleFieldChange('kpiKey', event.target.value)} required />
            </label>
            <label>
              Name
              <input value={form.name} onChange={(event) => handleFieldChange('name', event.target.value)} required />
            </label>
            <label>
              Category
              <input value={form.category} onChange={(event) => handleFieldChange('category', event.target.value)} required />
            </label>
            <label>
              Section
              <input value={form.section} onChange={(event) => handleFieldChange('section', event.target.value)} required />
            </label>
            <label>
              Unit
              <input value={form.unit} onChange={(event) => handleFieldChange('unit', event.target.value)} required />
            </label>
            <label>
              Format
              <select value={form.formatType} onChange={(event) => handleFieldChange('formatType', event.target.value)}>
                {FORMAT_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <label>
              Comparison Logic
              <select value={form.comparisonLogic} onChange={(event) => handleFieldChange('comparisonLogic', event.target.value)}>
                {COMPARISON_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <label>
              Display Order
              <input type="number" value={form.sortOrder} onChange={(event) => handleFieldChange('sortOrder', event.target.value)} />
            </label>
            <label className="director-form-span">
              Description
              <textarea value={form.description} onChange={(event) => handleFieldChange('description', event.target.value)} rows="3" />
            </label>
            <div className="director-permissions director-form-span">
              <span>Options</span>
              <div className="director-checkbox-grid">
                <label className="director-checkbox">
                  <input type="checkbox" checked={form.isDistributable} onChange={(event) => handleFieldChange('isDistributable', event.target.checked)} />
                  <span>Distributable</span>
                </label>
                <label className="director-checkbox">
                  <input type="checkbox" checked={form.supportsMonthlyGoal} onChange={(event) => handleFieldChange('supportsMonthlyGoal', event.target.checked)} />
                  <span>Monthly Targets</span>
                </label>
                <label className="director-checkbox">
                  <input type="checkbox" checked={form.supportsYearlyGoal} onChange={(event) => handleFieldChange('supportsYearlyGoal', event.target.checked)} />
                  <span>Yearly Goals</span>
                </label>
                <label className="director-checkbox">
                  <input type="checkbox" checked={form.showOnDashboard} onChange={(event) => handleFieldChange('showOnDashboard', event.target.checked)} />
                  <span>Show on Dashboard</span>
                </label>
              </div>
            </div>
            <div className="director-form-actions director-form-actions-spread director-form-span">
              <Button type="submit" loading={isSubmitting} loadingLabel="Saving…">
                {editingId ? 'Save Changes' : 'Add KPI'}
              </Button>
              <Button size="sm" variant="secondary" onClick={closeModal}>
                Cancel
              </Button>
            </div>
          </form>
        </DirectorModal>
      ) : null}
    </div>
  );
}

export default DirectorKpisPanel;
